/**
 * SiteDetail — displays site info, publish button, and build history.
 *
 * Fetches site details and builds for the given :id route param.
 * Auto-polls builds every 5 seconds when any build is queued or running.
 * Each build row is expandable to show the full build log.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { StatusBadge } from '../components/StatusBadge.tsx';
import { BuildLog } from '../components/BuildLog.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import type { Build, Site } from '../types.ts';

/** Format an ISO date string for display using the given locale. */
const formatDate = (iso: string, locale: string): string => {
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/** Format an ISO date string with time for build timestamps. */
const formatDateTime = (iso: string | null, locale: string): string => {
  if (!iso) return '--';
  try {
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/** Compute a human-readable duration between two ISO timestamps. */
const formatDuration = (start: string | null, end: string | null): string => {
  if (!start || !end) return '--';
  try {
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (ms < 1000) return `${ms}ms`;
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  } catch {
    return '--';
  }
};

/** Check whether any build in the list is still in progress. */
const hasActiveBuild = (builds: Build[]): boolean =>
  builds.some((b) => b.status === 'queued' || b.status === 'running');

export const SiteDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [expandedBuildId, setExpandedBuildId] = useState<string | null>(null);

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  const {
    data: siteResponse,
    isLoading: siteLoading,
    error: siteError,
  } = useQuery({
    queryKey: ['site', id],
    queryFn: () => api.sites.get(id!),
    enabled: !!id,
  });

  const site: Site | undefined = siteResponse?.data;

  const {
    data: buildsResponse,
    isLoading: buildsLoading,
    error: buildsError,
  } = useQuery({
    queryKey: ['builds', id],
    queryFn: () => api.builds.list(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const builds: Build[] = query.state.data?.data ?? [];
      return hasActiveBuild(builds) ? 5000 : false;
    },
  });

  const builds: Build[] = buildsResponse?.data ?? [];

  // -----------------------------------------------------------------------
  // Mutations
  // -----------------------------------------------------------------------

  const publishMutation = useMutation({
    mutationFn: () => api.sites.publish(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['builds', id] });
    },
  });

  // -----------------------------------------------------------------------
  // Toggle build log expansion
  // -----------------------------------------------------------------------

  const toggleBuild = (buildId: string) => {
    setExpandedBuildId((prev) => (prev === buildId ? null : buildId));
  };

  // -----------------------------------------------------------------------
  // Loading & error states
  // -----------------------------------------------------------------------

  if (siteLoading) {
    return (
      <div className="loading-page">
        <div className="spinner spinner--lg" aria-label={t('siteDetail.loading')} />
        <span>{t('siteDetail.loading')}</span>
      </div>
    );
  }

  if (siteError) {
    return (
      <div className="alert alert--error" role="alert">
        {t('siteDetail.loadError', { error: siteError instanceof Error ? siteError.message : t('common.error') })}
      </div>
    );
  }

  if (!site) {
    return (
      <div className="alert alert--error" role="alert">
        {t('siteDetail.notFound')}
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  const liveSiteUrl = `https://${site.subdomain}.refineweb.site`;
  const locale = i18n.language;

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <div>
          <h2 className="page-header__title">{site.subdomain}</h2>
          <p className="page-header__description">
            <a href={liveSiteUrl} target="_blank" rel="noopener noreferrer">
              {site.subdomain}.refineweb.site
            </a>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <Link to={`/sites/${id}/settings`} className="btn btn--secondary">
            {t('siteDetail.settings')}
          </Link>
          <button
            className="btn btn--primary btn--lg"
            onClick={() => publishMutation.mutate()}
            disabled={publishMutation.isPending}
          >
            {publishMutation.isPending ? (
              <>
                <span className="spinner" aria-hidden="true" />
                {t('siteDetail.publishing')}
              </>
            ) : (
              t('siteDetail.publish')
            )}
          </button>
        </div>
      </div>

      {publishMutation.error && (
        <div className="alert alert--error" role="alert">
          {publishMutation.error instanceof Error
            ? publishMutation.error.message
            : t('siteDetail.publishError')}
        </div>
      )}

      {publishMutation.isSuccess && (
        <div className="alert alert--success" role="status">
          {t('siteDetail.buildQueued')}
        </div>
      )}

      {/* Site info panel */}
      <div className="card mb-6">
        <div className="card__header">
          <h3 className="card__title">{t('siteDetail.info.title')}</h3>
        </div>
        <div className="card__body">
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item__label">{t('siteDetail.info.subdomain')}</span>
              <span className="detail-item__value">
                <a href={liveSiteUrl} target="_blank" rel="noopener noreferrer">
                  {site.subdomain}.refineweb.site
                </a>
              </span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">{t('siteDetail.info.googleSheet')}</span>
              <span className="detail-item__value font-mono truncate">
                {site.sheetId}
              </span>
              <a
                href={`https://docs.google.com/spreadsheets/d/${site.sheetId}/edit`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: 'var(--text-sm)', marginTop: 'var(--space-1)', display: 'inline-block' }}
              >
                {t('siteDetail.info.openSheet')}
              </a>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">{t('siteDetail.info.theme')}</span>
              <span className="detail-item__value">{site.themeId}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item__label">{t('siteDetail.info.created')}</span>
              <span className="detail-item__value">
                {formatDate(site.createdAt, locale)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Build history */}
      <div className="card mb-6">
        <div className="card__header">
          <h3 className="card__title">{t('siteDetail.builds.title')}</h3>
        </div>

        {buildsLoading ? (
          <div className="card__body">
            <div className="loading-page" style={{ minHeight: '120px' }}>
              <div className="spinner" aria-label={t('siteDetail.builds.loading')} />
              <span>{t('siteDetail.builds.loading')}</span>
            </div>
          </div>
        ) : buildsError ? (
          <div className="card__body">
            <div className="alert alert--error" role="alert">
              {t('siteDetail.builds.loadError', { error: buildsError instanceof Error ? buildsError.message : t('common.error') })}
            </div>
          </div>
        ) : builds.length === 0 ? (
          <div className="card__body">
            <EmptyState
              title={t('siteDetail.builds.empty.title')}
              description={t('siteDetail.builds.empty.description')}
            />
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t('siteDetail.builds.status')}</th>
                  <th>{t('siteDetail.builds.started')}</th>
                  <th>{t('siteDetail.builds.ended')}</th>
                  <th>{t('siteDetail.builds.duration')}</th>
                  <th>{t('siteDetail.builds.log')}</th>
                </tr>
              </thead>
              <tbody>
                {builds.map((build) => (
                  <BuildRow
                    key={build.id}
                    build={build}
                    locale={locale}
                    isExpanded={expandedBuildId === build.id}
                    onToggle={() => toggleBuild(build.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Danger zone */}
      <div className="card">
        <div className="card__header">
          <h3 className="card__title">{t('siteDetail.dangerZone.title')}</h3>
        </div>
        <div className="card__body">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <strong style={{ fontSize: 'var(--text-sm)' }}>
                {t('siteDetail.dangerZone.customDomains')}
              </strong>
              <p
                style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-gray-500)',
                  marginTop: 'var(--space-1)',
                }}
              >
                {t('siteDetail.dangerZone.customDomainsDescription')}
              </p>
            </div>
            <Link to="/domains" className="btn btn--secondary">
              {t('siteDetail.dangerZone.manageDomains')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BuildRow — single expandable row in the build history table
// ---------------------------------------------------------------------------

interface BuildRowProps {
  build: Build;
  locale: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const BuildRow = ({ build, locale, isExpanded, onToggle }: BuildRowProps) => {
  const { t } = useTranslation();

  return (
    <>
      <tr>
        <td>
          <StatusBadge status={build.status} />
        </td>
        <td>{formatDateTime(build.startedAt, locale)}</td>
        <td>{formatDateTime(build.endedAt, locale)}</td>
        <td>{formatDuration(build.startedAt, build.endedAt)}</td>
        <td>
          {build.logs ? (
            <button
              className="expandable__trigger"
              onClick={onToggle}
              aria-expanded={isExpanded}
              type="button"
            >
              <span
                className={`expandable__arrow ${isExpanded ? 'expandable__arrow--open' : ''}`}
                aria-hidden="true"
              >
                &#9654;
              </span>
              {isExpanded ? t('siteDetail.builds.hideLog') : t('siteDetail.builds.viewLog')}
            </button>
          ) : (
            <span style={{ color: 'var(--color-gray-400)', fontSize: 'var(--text-xs)' }}>
              {t('siteDetail.builds.noLogs')}
            </span>
          )}
        </td>
      </tr>
      {isExpanded && build.logs && (
        <tr>
          <td colSpan={5} style={{ padding: 0 }}>
            <div style={{ padding: 'var(--space-3) var(--space-4)' }}>
              <BuildLog
                logs={build.logs}
                autoScroll={build.status === 'running'}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
